'use client'

// R21: one per-document coordinator shared by route and overlay owners.
// Native history receives only a short marker. All UI snapshots remain bounded in RAM.
const STATE_KEY = '__ql7UiStepR20'
const VERSION = 2
const DEFAULT_LIMIT = 64
const controllers = new WeakMap()

function routeKey(win) {
  return `${win.location.origin}${win.location.pathname}`
}

function taggedState(history, marker) {
  const current = history.state
  return { ...(current && typeof current === 'object' && !Array.isArray(current) ? current : {}), [STATE_KEY]: marker }
}

function makeCoordinator(win) {
  let scope = routeKey(win)
  const session = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`
  const owners = new Map()
  const entries = []
  let cursor = 0
  let sequence = 0
  let paused = false
  let applying = false
  let limit = DEFAULT_LIMIT
  let active = false
  const pending = new Map()
  const alive = () => routeKey(win) === scope
  const mark = (id) => ({ version: VERSION, session, id })
  const readAll = () => {
    const states = {}
    for (const [name, adapter] of owners) {
      try { states[name] = adapter.read() } catch { /* fail closed */ }
    }
    return states
  }
  const currentMarker = () => win.history.state?.[STATE_KEY]
  const isCurrent = () => {
    const value = currentMarker()
    return value?.version === VERSION && value?.session === session && value?.id === entries[cursor]?.id
  }
  const rebase = () => {
    if (!alive()) return false
    entries.length = 0
    cursor = 0
    pending.clear()
    sequence += 1
    try { win.history.replaceState(taggedState(win.history, mark(sequence)), '', win.location.href) } catch { return false }
    entries.push({ id: sequence, states: readAll() })
    return true
  }
  const ensureCurrent = () => {
    if (isCurrent()) return true
    // Next.js can replaceState while hydrating without preserving our marker.
    // Repair only an unmarked current entry; never hijack another session/route.
    if (currentMarker() || !alive() || !entries[cursor]) return false
    try {
      win.history.replaceState(taggedState(win.history, mark(entries[cursor].id)), '', win.location.href)
    } catch { return false }
    return isCurrent()
  }
  const onPop = (event) => {
    if (paused || !alive()) return
    const m = event?.state?.[STATE_KEY]
    if (m?.version !== VERSION || m?.session !== session) return
    const next = entries.findIndex((entry) => entry.id === m.id)
    if (next < 0) return
    const previous = entries[cursor]?.states || {}
    cursor = next
    pending.clear()
    const states = entries[next].states
    applying = true
    try {
      // Restores each participating owner: closing a top overlay before rewinding the forum.
      for (const [name, adapter] of owners) {
        if (!Object.prototype.hasOwnProperty.call(states, name)) continue
        if (JSON.stringify(states[name]) === JSON.stringify(previous[name])) continue
        try { adapter.restore(states[name]) } catch { /* one owner cannot poison others */ }
      }
      for (const [name, adapter] of owners) {
        try { adapter.onPosition?.(next, entries.map((entry) => entry.states[name])) } catch {}
      }
    } finally {
      applying = false
    }
  }
  const onHide = () => {
    paused = true
    entries.length = 0
    cursor = 0
    pending.clear()
  }
  const onShow = (event) => {
    if (!paused || !event?.persisted) return
    paused = false
    rebase()
  }
  const stop = () => {
    if (!active) return
    active = false
    win.removeEventListener('popstate', onPop)
    win.removeEventListener('pagehide', onHide)
    win.removeEventListener('pageshow', onShow)
    entries.length = 0
    owners.clear()
    controllers.delete(win)
  }
  const start = () => {
    if (active) return true
    if (!rebase()) return false
    active = true
    win.addEventListener('popstate', onPop)
    win.addEventListener('pagehide', onHide)
    win.addEventListener('pageshow', onShow)
    return true
  }
  return {
    add(owner, adapter, requestedLimit) {
      if (owners.has(owner)) return false
      if (!alive()) { scope = routeKey(win); if (active) rebase() }
      limit = Math.min(limit, Math.max(2, Math.min(DEFAULT_LIMIT, Math.trunc(Number(requestedLimit) || DEFAULT_LIMIT))))
      owners.set(owner, adapter)
      if (!start()) { owners.delete(owner); return false }
      if (entries[cursor]) {
        try {
          const initial = adapter.read()
          // React may remount a route subtree during a native Back. Rehydrate the
          // current bounded snapshot instead of resetting it to the mount default.
          const old = entries[cursor].states[owner]
          for (const entry of entries) {
            if (!Object.prototype.hasOwnProperty.call(entry.states, owner)) entry.states[owner] = initial
          }
          if (old !== undefined && JSON.stringify(old) !== JSON.stringify(initial)) {
            try { adapter.restore(old) } catch {}
          }
        } catch {}
      }
      return true
    },
    remove(owner, adapter) {
      if (owners.get(owner) !== adapter) return
      owners.delete(owner)
      // Keep at most the bounded session snapshots for a same-route React remount.
      // They are cleared by rebase on route change, pagehide or the last-owner dispose.
      if (!alive()) for (const entry of entries) delete entry.states[owner]
      if (!owners.size) stop()
    },
    checkpoint(owner) {
      // A surface may open automatically without pushing a history entry.
      // Before its next deliberate navigation, store that *current* surface
      // in the current entry; do not manufacture an extra browser step.
      if (!active || paused || applying || !owners.has(owner) || !ensureCurrent()) return false
      try {
        entries[cursor].states[owner] = owners.get(owner).read()
        pending.delete(owner)
        return true
      } catch { return false }
    },
    record(owner, nextState) {
      if (!active || paused || applying || !owners.has(owner)) return false
      if (!alive()) { scope = routeKey(win); if (!rebase()) return false }
      if (!ensureCurrent()) return false
      const states = { ...entries[cursor].states, ...readAll() }
      if (nextState !== undefined) {
        if (JSON.stringify(entries[cursor].states[owner]) === JSON.stringify(nextState)) return false
        states[owner] = nextState
      }
      // pushState truncates the native forward branch; mirror that in memory.
      if (entries.length >= limit && cursor === entries.length - 1) return false
      const id = ++sequence
      try { win.history.pushState(taggedState(win.history, mark(id)), '', win.location.href) } catch { return false }
      entries.splice(cursor + 1)
      entries.push({ id, states })
      cursor = entries.length - 1
      if (nextState === undefined) pending.set(owner, id)
      return true
    },
    sync(owner) {
      if (!active || paused || applying || !ensureCurrent() || !owners.has(owner)) return
      // Only the owner of a freshly recorded, not-yet-committed navigation may sync.
      // popstate clears this grant, preventing stale React commits from erasing restores.
      if (pending.get(owner) !== entries[cursor].id) return
      try {
        const actual = owners.get(owner).read()
        if (JSON.stringify(actual) === JSON.stringify(entries[cursor].states[owner])) return
        entries[cursor].states[owner] = actual
      } catch { return }
      pending.delete(owner)
    },
    back() {
      if (!active || paused || applying || !alive() || cursor <= 0 || !isCurrent()) return false
      win.history.back()
      return true
    },
    debug: () => ({ size: entries.length, position: cursor, limit, paused, running: active, owners: owners.size }),
  }
}

export function createBrowserStepHistory({ win, owner, read, restore, onPosition, limit = DEFAULT_LIMIT }) {
  if (!win?.history || !owner || typeof read !== 'function' || typeof restore !== 'function') {
    return { start: () => false, record: () => false, checkpoint: () => false, sync: () => {}, back: () => false, dispose: () => {}, debug: () => ({ size: 0 }) }
  }
  const adapter = { read, restore, onPosition }
  let coordinator = null
  return {
    start() {
      if (process.env.NEXT_PUBLIC_QL7_BROWSER_STEPS_ENABLED === '0') return false
      if (coordinator) return true
      const shared = controllers.get(win) || makeCoordinator(win)
      if (!shared.add(owner, adapter, limit)) return false
      controllers.set(win, shared)
      coordinator = shared
      return true
    },
    record: (nextState) => coordinator?.record(owner, nextState) || false,
    checkpoint: () => coordinator?.checkpoint(owner) || false,
    sync: () => coordinator?.sync(owner),
    back: () => coordinator?.back() || false,
    dispose() {
      if (!coordinator) return
      coordinator.remove(owner, adapter)
      coordinator = null
    },
    debug: () => coordinator?.debug() || { size: 0 },
  }
}
